import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight } from 'lucide-react'
import { leadsAPI } from './api'
import type { NaceCode } from './api'
import { useLatest } from '../Bruktbil/useLatest'

type SortCol = 'nace_kode' | 'org_count' | 'total_vehicles' | 'scored_org_count'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props {
  page: number
  onPageChange: (page: number) => void
  markedsomradeIds?: string
  filterMineNace?: boolean
}

const PAGE_SIZE = 20

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

function totalVehicles(r: NaceCode) {
  return (r.total_owned ?? 0) + (r.total_leased ?? 0)
}

export default function NaceKoderTab({ page, onPageChange, markedsomradeIds, filterMineNace }: Props) {
  const { t } = useTranslation('leads')
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState<NaceCode[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<Sort>({ col: 'total_vehicles', dir: 'desc' })

  // Mine NACE toggle state
  const [mineNaceKoder, setMineNaceKoder] = useState<Set<string>>(new Set())

  useEffect(() => {
    leadsAPI.getMineNaceKoder()
      .then(res => setMineNaceKoder(new Set(res.data.map(k => k.nace_kode))))
      .catch(() => {})
  }, [])

  useEffect(() => { onPageChange(1) }, [markedsomradeIds])

  // useLatest: a superseded answer must not replace a newer one (#901).
  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getNaceCodes({
      markedsomrade_ids: markedsomradeIds,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then(res => { if (alive()) { setRows(res.data.items); setTotal(res.data.total) } })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [markedsomradeIds, page])

  function handleSort(col: SortCol) {
    setSort(s => {
      if (s?.col === col) return s.dir === 'desc' ? { col, dir: 'asc' } : null
      return { col, dir: 'desc' }
    })
  }

  function sortValue(r: NaceCode, col: SortCol): string | number {
    if (col === 'nace_kode') return r.nace_kode
    if (col === 'org_count') return r.org_count
    if (col === 'scored_org_count') return r.scored_org_count ?? 0
    return totalVehicles(r)
  }

  const filtered = filterMineNace ? rows.filter(r => mineNaceKoder.has(r.nace_kode)) : rows

  const sorted = sort
    ? [...filtered].sort((a, b) => {
        const va = sortValue(a, sort.col)
        const vb = sortValue(b, sort.col)
        if (typeof va === 'string' && typeof vb === 'string') return sort.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
        return sort.dir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number)
      })
    : filtered

  async function toggleMineNace(nace_kode: string) {
    try {
      if (mineNaceKoder.has(nace_kode)) {
        await leadsAPI.deleteMineNaceKode(nace_kode)
        setMineNaceKoder(prev => { const n = new Set(prev); n.delete(nace_kode); return n })
      } else {
        await leadsAPI.addMineNaceKode(nace_kode)
        setMineNaceKoder(prev => new Set(prev).add(nace_kode))
      }
    } catch {
      // silently fail
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  if (error) return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>

  return (
    <div className="card p-6">
      {loading && <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm mb-4" />}

      {!loading && (
        <>
          {rows.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('naceKoder.empty')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="data-table data-table-compact">
                  <thead>
                    <tr>
                      <th className="text-center w-10"></th>
                      <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => handleSort('nace_kode')}>
                        {t('naceKoder.naceKode')} <SortIcon col="nace_kode" sort={sort} />
                      </th>
                      <th className="text-left">{t('naceKoder.beskrivelse')}</th>
                      {/* Two different populations, and the gap between them is
                          wide — 549 against 261 for 43.210 in Oslo. Unlabelled,
                          the larger one reads as a promise that "Se" breaks. */}
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => handleSort('org_count')}
                          title={t('naceKoder.orgsInAreaTitle')}>
                        {t('naceKoder.orgsInArea')} <SortIcon col="org_count" sort={sort} />
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => handleSort('scored_org_count')}
                          title={t('naceKoder.orgsScoredTitle')}>
                        {t('naceKoder.orgsScored')} <SortIcon col="scored_org_count" sort={sort} />
                      </th>
                      <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => handleSort('total_vehicles')}>
                        {t('naceKoder.kjoretoy')} <SortIcon col="total_vehicles" sort={sort} />
                      </th>
                      <th className="text-center">{t('common.details')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, idx) => (
                      <tr key={row.nace_kode}
                          className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}>
                        <td className="text-center">
                          <button onClick={() => toggleMineNace(row.nace_kode)}
                            className="text-lg hover:scale-110 transition-transform"
                            title={mineNaceKoder.has(row.nace_kode) ? t('naceKoder.removeMine') : t('naceKoder.addMine')}>
                            {mineNaceKoder.has(row.nace_kode) ? '\u2B50' : '\u2606'}
                          </button>
                        </td>
                        <td className="font-mono text-sm">{row.nace_kode}</td>
                        <td className="text-sm text-gray-700 dark:text-gray-300">{row.nace_beskrivelse || '\u2014'}</td>
                        <td className="text-right">{(row.org_count ?? 0).toLocaleString('nb-NO')}</td>
                        <td className="text-right">{(row.scored_org_count ?? 0).toLocaleString('nb-NO')}</td>
                        <td className="text-right font-bold">{totalVehicles(row).toLocaleString('nb-NO')}</td>
                        <td className="text-center">
                          <button
                            onClick={() => navigate(
                              // markedsomradet ma folge med i URL-en, ikke bare
                              // i router-state: detaljsiden skal tale refresh og
                              // deling, og uten scope viser den hele landet
                              `/leads/nace/${encodeURIComponent(row.nace_kode)}${markedsomradeIds ? `?mo=${encodeURIComponent(markedsomradeIds)}` : ''}`,
                              { state: { from: location.pathname + location.search } },
                            )}
                            className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            <span className="text-xs font-medium">{t('common.see')}</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
                  <span>{t('common.pageOf', { page, pages })} &middot; {t('common.totalCount', { n: total.toLocaleString('nb-NO') })}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onPageChange(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('common.prev')}
                    </button>
                    <button
                      onClick={() => onPageChange(Math.min(pages, page + 1))}
                      disabled={page === pages}
                      className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
